/**
 * The individual table: every point you were on court for is yours.
 *
 * Lives here rather than in the app because two things now read it. The standings screen
 * shows it, and Mexicano *draws from* it — round k+1's pairings are 1st with 4th against 2nd
 * with 3rd, straight down this order. Two implementations of "who is winning" would mean a
 * table that disagreed with the fixtures generated from it, which is the one bug nobody in
 * the hall could be talked out of.
 *
 * Ranked on **points**, then matches won, then points conceded. That order is the format
 * rather than a preference: a night of americano or mexicano is played to a fixed number of
 * points per match precisely so the margin counts. The member id breaks a genuine three-way
 * tie, so the same results always produce the same order — a draw that reshuffled itself
 * between a preview and a commit would be worse than no preview.
 */

/** A game's score. Structural, so `@rally/rating`'s `Game` satisfies it without a dependency. */
export interface GameScore {
  a: number;
  b: number;
}

/** As much of a fixture as a table needs: who played, and what happened. */
export interface PlayedFixture {
  playersA?: string[] | null;
  playersB?: string[] | null;
  result?: { games: GameScore[]; winner: "A" | "B" } | null;
}

export interface PlayerRow {
  memberId: string;
  played: number;
  won: number;
  /** Points scored by whichever side this player was on, across every match. */
  points: number;
  /** Points conceded, which is the third tiebreak and the one that punishes a hiding. */
  against: number;
}

export function rankPlayers(fixtures: PlayedFixture[]): PlayerRow[] {
  const rows = new Map<string, PlayerRow>();

  const row = (memberId: string) => {
    const existing = rows.get(memberId);
    if (existing) return existing;
    const fresh: PlayerRow = { memberId, played: 0, won: 0, points: 0, against: 0 };
    rows.set(memberId, fresh);
    return fresh;
  };

  for (const fixture of fixtures) {
    if (!fixture.result || !fixture.playersA?.length || !fixture.playersB?.length) continue;

    const tally = countPoints(fixture.result.games);
    const sides = [
      { players: fixture.playersA, for: tally.a, against: tally.b, won: "A" },
      { players: fixture.playersB, for: tally.b, against: tally.a, won: "B" },
    ] as const;

    for (const side of sides) {
      for (const memberId of side.players) {
        const entry = row(memberId);
        entry.played += 1;
        entry.points += side.for;
        entry.against += side.against;
        if (fixture.result.winner === side.won) entry.won += 1;
      }
    }
  }

  return [...rows.values()].sort(
    (x, y) =>
      y.points - x.points ||
      y.won - x.won ||
      x.against - y.against ||
      x.memberId.localeCompare(y.memberId),
  );
}

/** Points across every game of a match, both sides. */
function countPoints(games: GameScore[]) {
  let a = 0;
  let b = 0;
  for (const game of games) {
    a += game.a;
    b += game.b;
  }
  return { a, b };
}
